import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import _ from 'lodash';
import showdown from 'showdown';

import {
  Wrapper,
  Container,
  HeroImageWrapper,
  HeroImage,
  Title,
  SubTitle,
  TitleText,
  Sections,
  SectionWrapper,
  SectionTitle,
  SectionText,
  SectionDescription,
  SectionDescriptionLine,
  SectionDescriptionThirdLine,
  SectionImageWrapper,
  SectionTextWrapper,
  SeeMore,
  ImageArticleWrapper,
  CarouselWrapper,
  CarouselContentWrapper,
  CarouselContent,
  CarouselContentTitle,
  CarouselContentDescription,
  CarouselContentImageWrapper,
  CarouselContentTextWrapper,
  CarouselContentTextContent,
  CarouselListWrapper,
  CarouselListThumbnail,
  CarouselListThumbnailImage,
  Hr,
  ButtonWrapper,
  Button,
  MarkdownStyle,
  YoutubeSectionWrapper,
  YoutubeSection,
} from './ArticlePage.style';
import Placeholder from '@/components/Placeholder';
import Image from '@/components/ContentfulImage';
import Slick from '@/components/Slider/Slick';
import BreadCrumbMenu from '@/components/BreadCrumbMenus/';
import Markdown from '@/components/Markdown';
import { getScreenWidth, generateKey, makeCanonicalTagObj, makeHreflangTagArr } from '@/utils';

import { TABLET_P, TABLET_L, DESKTOP, MOBILE_PADDING } from '@/constants';

class ArticlePage extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      // 選択したカルーセルコンテント
      selectedEntryId: '-1',
      // セクション > description 表示時 >「see more」ボタン押下状態 (true: 未押下、false: 押下済)
      isSeeMoreClicked: false,
      // ブラウザ伸縮時に更新されたスクリーンサイズ (スタイル用)
      syncingScreenSize: null,
    };

    // セクション > description > 「see more」ボタン押下後の表示対応
    this.displayWholeLines = this.displayWholeLines.bind(this);

    // セクション > description 表示時 > 3行目「see more」挿入対応
    this.displaySplitLines = this.displaySplitLines.bind(this);
  }

  componentDidMount() {
    // ブラウザの横幅をマウスなどによる伸縮に追随させた時に、スクリーンサイズが適切に更新されない場合があるため、リサイズイベントを走らせる
    window.addEventListener('resize', () => this.setState({ syncingScreenSize: getScreenWidth() }));
  }

  /**
   * マークダウンから HTML に変換する
   */
  convertMarkdownToHtml = new showdown.Converter({
    noHeaderId: true,
  });

  /**
   * セクション > description > 「see more」ボタン押下時に呼ばれる
   *   * <br> タグがある場合、空文字に変換する
   */
  displayWholeLines = description => {
    if (!description) return;

    return description
      .split(/<br>/g)
      .map((text, index) => (
        <SectionDescriptionLine
          key={generateKey('description-whole-line', index)}
          dangerouslySetInnerHTML={{ __html: this.convertMarkdownToHtml.makeHtml(text) }}
        />
      ));
  };

  /**
   * セクション > description 表示時に呼ばれる
   *   * <br> タグがある場合、description の3行目に「see more」挿入を行う、且つ、<br> を改行コードに変換する
   */
  displaySplitLines = description => {
    if (!description) return;

    // 3 行目を意味する局所変数
    const thirdLineIndex = 2;

    return description.split(/<br>/g).map((text, index) => {
      if (index < thirdLineIndex) {
        return (
          <SectionDescriptionLine
            key={generateKey('description-partial-line', index)}
            dangerouslySetInnerHTML={{ __html: this.convertMarkdownToHtml.makeHtml(text) }}
          />
        );
      }
      if (index === thirdLineIndex) {
        return (
          <SectionDescriptionLine key={generateKey('description-line', index)} className="thirdLine">
            <SectionDescriptionThirdLine
              dangerouslySetInnerHTML={{ __html: this.convertMarkdownToHtml.makeHtml(text) }}
            />
            {` `}
            <SeeMore onClick={() => this.setState({ isSeeMoreClicked: true })}>see more</SeeMore>
          </SectionDescriptionLine>
        );
      }
    });
  };

  render() {
    // Slick スライダー > サムネイル表示数 (SP、PC 共通)
    const slidesToShowNumber = 5;
    // Slick スライダー > スワイプ毎のページング数 (SP、PC 共通)
    const swipeToSlideNumber = 1;
    // Slick スライダー > title と description、及び、それぞれの余白を含めた高さ (SP 版)
    const carouselTitleDescriptionHeight = 276;
    // Slick スライダー > 2段スライダー > 下段 > サムネイル画像 縦横幅
    const thumbnailDimension = 80;

    const { data, locale, screenSize } = this.props;
    const { selectedEntryId, isSeeMoreClicked, syncingScreenSize } = this.state;
    return (
      <Wrapper>
        {_.get(data, 'region') && (
          <Helmet
            link={[
              makeCanonicalTagObj(locale, `/${data.breadcrumbs.Breadcrumbs2.path}/${data.slug}`),
              ...makeHreflangTagArr(data.region, `/${data.breadcrumbs.Breadcrumbs2.path}/${data.slug}`),
            ]}
          />
        )}
        {_.get(data, 'metaData') && (
          <Helmet
            title={_.get(data.metaData, 'title')}
            meta={[
              {
                name: 'description',
                content: _.get(data.metaData, 'description'),
              },
              {
                name: 'keyword',
                content: _.get(data.metaData, 'keyword'),
              },
              // TODO: OGP タグは一旦保留 (2020年01月07日)
              // {
              //   property: 'og:url',
              //   content: _.get(data.metaData, 'url'),
              // },
              // {
              //   property: 'og:type',
              //   content: _.get(data.metaData, 'type'),
              // },
              // {
              //   property: 'og:title',
              //   content: _.get(data.metaData, 'title'),
              // },
              // {
              //   property: 'og:image',
              //   content: _.get(data.metaData, 'image.file.url'),
              // },
              // {
              //   property: 'og:description',
              //   content: _.get(data.metaData, 'description'),
              // },
              // {
              //   property: 'og:site_name',
              //   content: _.get(data.metaData, 'siteName'),
              // },
            ]}
          />
        )}
        <Container>
          {data.heroImages &&
            data.heroImages.length && (
              <HeroImageWrapper>
                <HeroImage src={data.heroImages[0].file.url} alt="" />
              </HeroImageWrapper>
            )}

          {_.get(data, 'youtubeSection') && (
            <YoutubeSectionWrapper>
              <Markdown src={data.youtubeSection} Component={YoutubeSection} />
            </YoutubeSectionWrapper>
          )}

          <Title>
            {!_.get(data, 'title') ? (
              <Placeholder w="80%" h="100px" />
            ) : (
              <React.Fragment>
                {data.title
                  .split(/<br>/g)
                  .map((text, index) => <TitleText key={generateKey('title', index)}>{text}</TitleText>)}
              </React.Fragment>
            )}
          </Title>
          {!_.get(data, 'sections') ? null : (
            <React.Fragment>
              {/* SECTIONS 表示部分 */}
              <Sections className="sectionsPart">
                {data.sections.map((datum, index) => (
                  <SectionWrapper key={generateKey('section-content', index)} className="sectionsPart">
                    {!!(_.get(datum, 'contentType') === 'inspirationArticleTitle' && _.get(datum, 'title')) && (
                      <React.Fragment>
                        <SubTitle>{datum.title}</SubTitle>
                        <Hr />
                      </React.Fragment>
                    )}
                    {!!(_.get(datum, 'contentType') === 'inspirationArticleText' && _.get(datum, 'description')) && (
                      <SectionText>{datum.description}</SectionText>
                    )}
                    {!!(
                      _.get(datum, 'contentType') === 'inspirationArticleImageArticle' &&
                      _.get(datum, 'title') &&
                      _.get(datum, 'description')
                    ) && (
                      <ImageArticleWrapper direction={(syncingScreenSize || screenSize) > TABLET_L ? 'row' : 'column'}>
                        {!(_.get(datum, 'image.file.url') && _.get(datum, 'image.file.details')) ? (
                          <Placeholder w="100%" h="100%" />
                        ) : (
                          <SectionImageWrapper
                            left={index % 2 !== 0}
                            width={(syncingScreenSize || screenSize) > TABLET_L ? 'auto' : '100%'}
                            height={
                              (syncingScreenSize || screenSize) > TABLET_L
                                ? 'auto'
                                : `${(syncingScreenSize || screenSize) - MOBILE_PADDING * 2}px`
                            }
                          >
                            <Image
                              src={datum.image}
                              options={{
                                w:
                                  (syncingScreenSize || screenSize) > TABLET_L
                                    ? 'auto'
                                    : `${(syncingScreenSize || screenSize) - MOBILE_PADDING * 2}px`,
                                h:
                                  (syncingScreenSize || screenSize) > TABLET_L
                                    ? 'auto'
                                    : `${(syncingScreenSize || screenSize) - MOBILE_PADDING * 2}px`,
                                minHeight:
                                  (syncingScreenSize || screenSize) > TABLET_L
                                    ? '500px'
                                    : `${(syncingScreenSize || screenSize) - MOBILE_PADDING * 2}px`,
                                minWidth:
                                  (syncingScreenSize || screenSize) > TABLET_L
                                    ? '500px'
                                    : `${(syncingScreenSize || screenSize) - MOBILE_PADDING * 2}px`,
                                size:
                                  (syncingScreenSize || screenSize) > TABLET_L
                                    ? [0, 0, 500, 500]
                                    : [
                                        0,
                                        0,
                                        (syncingScreenSize || screenSize) - MOBILE_PADDING * 2,
                                        (syncingScreenSize || screenSize) - MOBILE_PADDING * 2,
                                      ],
                                aspectRatio: 9 / 10,
                                objectFit: 'cover',
                              }}
                            />
                          </SectionImageWrapper>
                        )}
                        <SectionTextWrapper left={index % 2 !== 0}>
                          <SectionTitle>{datum.title}</SectionTitle>
                          <SectionDescription>
                            {!datum.description ? (
                              <Placeholder w="100%" h="100%" />
                            ) : (
                              <React.Fragment>
                                {!datum.seemore || isSeeMoreClicked
                                  ? this.displayWholeLines(datum.description)
                                  : this.displaySplitLines(datum.description)}
                              </React.Fragment>
                            )}
                          </SectionDescription>
                        </SectionTextWrapper>
                      </ImageArticleWrapper>
                    )}
                  </SectionWrapper>
                ))}
              </Sections>

              {/* スライダー表示部分 */}
              <Sections className="sliderPart">
                {data.sections.map((datum, index) => (
                  <SectionWrapper key={generateKey('section-content', index)}>
                    {!!(
                      _.get(datum, 'contentType') === 'inspirationArticleCarouselSection' && _.get(datum, 'title')
                    ) && (
                      <React.Fragment>
                        <SubTitle>{datum.title}</SubTitle>
                      </React.Fragment>
                    )}
                    {!!(
                      _.get(datum, 'contentType') === 'inspirationArticleCarouselSection' && _.get(datum, 'description')
                    ) && <SectionText>{datum.description}</SectionText>}
                    {!!(
                      _.get(datum, 'contentType') === 'inspirationArticleCarouselSection' &&
                      _.get(datum, 'contents') &&
                      datum.contents.length > 0
                    ) && (
                      <CarouselWrapper>
                        <CarouselListWrapper>
                          <Slick
                            items={datum.contents}
                            shouldSync
                            dimension={{
                              sliderWrapper: {
                                height:
                                  (syncingScreenSize || screenSize) > DESKTOP
                                    ? '500px'
                                    : (syncingScreenSize || screenSize) > TABLET_L
                                      ? '400px'
                                      : (syncingScreenSize || screenSize) > TABLET_P
                                        ? '300px'
                                        : `${(syncingScreenSize || screenSize) -
                                            MOBILE_PADDING * 2 +
                                            carouselTitleDescriptionHeight}px`,
                                slickSlideWidth:
                                  (syncingScreenSize || screenSize) > TABLET_P
                                    ? `${datum.contents.length * 96}px`
                                    : (syncingScreenSize || screenSize) - MOBILE_PADDING * 2,
                              },
                            }}
                            // スライダー設定
                            //   1. dots: true = 丸ポチを表示する
                            //   2. variableWidth: true = 横幅の制御を許容する
                            //   3. arrows: true = 前後ボタンを表示する
                            //   4. swipeToSlide: スワイプ毎のページング数
                            //   5. slidesToShow: サムネイル表示数
                            options={{
                              dots: true,
                              variableWidth: true,
                              arrows: false,
                              swipeToSlide: datum.contents.length,
                              slidesToShow: swipeToSlideNumber,
                            }}
                          >
                            <CarouselContentWrapper
                              key={`selectedThumbnail${_.findIndex(
                                datum.contents,
                                item => item.entryId === selectedEntryId
                              )}`}
                            >
                              {datum.contents.map(({ description, title, image }, index) => (
                                <CarouselContent key={generateKey('carousel-content-selected', index)}>
                                  {!_.get(image, 'file.url') ? (
                                    <Placeholder w="100%" h="100%" />
                                  ) : (
                                    <CarouselContentImageWrapper>
                                      <Image
                                        src={image}
                                        options={{
                                          w: '100%',
                                          h: 'auto',
                                          autoHeight: true,
                                          maxHeight: ['unset', '400px', '400px', '500px'],
                                          aspectRatio: 9 / 10,
                                          objectFit: 'cover',
                                        }}
                                      />
                                    </CarouselContentImageWrapper>
                                  )}
                                  <CarouselContentTextWrapper>
                                    <CarouselContentTextContent>
                                      <CarouselContentTitle>
                                        {title || <Placeholder w="100%" h="100%" />}
                                      </CarouselContentTitle>
                                      <CarouselContentDescription>
                                        {description ? (
                                          <Markdown src={description} Component={MarkdownStyle} />
                                        ) : (
                                          <Placeholder w="100%" h="100px" />
                                        )}
                                      </CarouselContentDescription>
                                    </CarouselContentTextContent>
                                  </CarouselContentTextWrapper>
                                </CarouselContent>
                              ))}
                            </CarouselContentWrapper>
                            {datum.contents.map(({ entryId, image }, index) => (
                              <CarouselListThumbnail
                                key={generateKey('carousel-content', index)}
                                // クロージャにしておく
                                onClick={() => this.setState({ selectedEntryId: entryId })}
                                margin={
                                  (screenSize - thumbnailDimension * (slidesToShowNumber - 1)) / slidesToShowNumber - 1
                                }
                              >
                                {_.get(image, 'file.url') ? (
                                  <CarouselListThumbnailImage src={image.file.url} alt="" />
                                ) : (
                                  <Placeholder w="100%" h="100%" />
                                )}
                              </CarouselListThumbnail>
                            ))}
                          </Slick>
                        </CarouselListWrapper>
                      </CarouselWrapper>
                    )}
                  </SectionWrapper>
                ))}
              </Sections>

              {/* 「戻る」ボタン表示部分 */}
              <Sections className="buttonPart">
                <ButtonWrapper>
                  {!(locale && _.get(data, 'breadcrumbs') && _.get(data, 'backButton')) ? (
                    <Placeholder w="100%" h="100%" />
                  ) : (
                    <Link
                      to={`/${locale}${data.breadcrumbs.Breadcrumbs2.path}`}
                      href={`/${locale}${data.breadcrumbs.Breadcrumbs2.path}`}
                    >
                      <Button>{data.backButton}</Button>
                    </Link>
                  )}
                </ButtonWrapper>
              </Sections>
            </React.Fragment>
          )}
        </Container>
        {!(locale && _.get(data, 'title') && _.get(data, 'breadcrumbs') && _.get(data, 'slug')) ? (
          <Placeholder
            w="200px"
            h="24px"
            m={(syncingScreenSize || screenSize) > TABLET_L ? '0 0 20px 40px' : '0 0 20px 20px'}
          />
        ) : (
          <BreadCrumbMenu
            currentPath={`/${locale}${data.breadcrumbs.Breadcrumbs2.path}/${data.slug}`}
            items={[
              {
                name: 'TOP',
                path: `/${locale}/`,
              },
              {
                name: data.breadcrumbs.Breadcrumbs1.name,
                path: `/${locale}/inspiration/`,
              },
              {
                name: data.breadcrumbs.Breadcrumbs2.name,
                path: `/${locale}${data.breadcrumbs.Breadcrumbs2.path}`,
              },
              {
                name: data.title.replace(/<br>/g, ' ').toUpperCase(),
                path: null,
              },
            ]}
          />
        )}
      </Wrapper>
    );
  }
}

ArticlePage.defaultProps = {
  data: { notLoaded: true, copy: {} },
  locale: null,
  screenSize: null,
};

ArticlePage.propTypes = {
  data: PropTypes.shape({}),
  locale: PropTypes.string,
  // スクリーンサイズ
  screenSize: PropTypes.number,
};

const mapState = () => ({
  // スクリーンサイズを実寸で取得する
  screenSize: getScreenWidth(),
});

export default connect(mapState)(ArticlePage);



// WEBPACK FOOTER //
// ./src/pages/InspirationPage/ArticlePage.js